import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { ExternalCommunicationDialogComponent } from '@components/system-setting/external-communication-dialog/external-communication-dialog.component';
import { FcuUnitServiece } from '@service/fcu-unit.service';
import { VesselService } from '@service/vessel.service';
import { FcuUnit, FcuUnitAliases, FcuUnitFull } from 'icreate/common/models/fcu-unit';
import { emptySlot, emptyValue, Slot } from 'icreate/common/models/slot';
import { Vessel } from 'icreate/common/models/vessel';
import { ValidationError } from 'icreate/common/validators';
import { firstValueFrom, Subject, takeUntil } from 'rxjs';
import { ExternalCommunicationService } from '@service/external-communication.service';
import { CeuUnitService } from '@service/ceu-unit.service';
import { communicationTypes, FuUnitWithUX } from 'icreate/common/models/fu-unit';
import { CeuUnit } from 'icreate/common/models/ceu-unit';
import { ExternalCommunicationWithConfig } from 'icreate/common/models/external-communication';
import { FcuUnitSocketService } from '@service/fcu-unit-socket.service';
import { MessageDialogService } from '@service/message-dialog.service';
import { SystemSettingTab } from '@src/app/models/system-setting';

@Component({
    selector: 'app-fcu-setting-view',
    templateUrl: './fcu-setting-view.component.html',
    styleUrls: ['./fcu-setting-view.component.css'],
    standalone: false,
})
export class FcuSettingViewComponent implements OnInit, OnDestroy {
    readonly EMPTY_SLOT = emptySlot;
    readonly EMPTY_VALUE = emptyValue;

    readonly SERIAL = communicationTypes.serial;

    vessel: Vessel;
    selectedFcuUnit: FcuUnit = undefined;
    fcuUnitList: FcuUnit[] = [];
    fuList: FuUnitWithUX[] = [];
    slotList: Slot[] = undefined;
    selectedSlotCounter: number | string = undefined;
    location: string = '';
    fcuALineAIp: string = '';
    fcuALineBIp: string = '';
    fcuAFieldNetLineAIp: string = '';
    fcuAFieldNetLineBIp: string = '';
    fcuACeuCommLineAIp: string = '';
    fcuACeuCommLineBIp: string = '';
    fcuBLineAIp: string = '';
    fcuBLineBIp: string = '';
    fcuBFieldNetLineAIp: string = '';
    fcuBFieldNetLineBIp: string = '';
    fcuBCeuCommLineAIp: string = '';
    fcuBCeuCommLineBIp: string = '';
    validationErrors: { [propertyName: string]: ValidationError | { message: string } } = {};
    isOpenOption: boolean = false;
    externalCommunication: ExternalCommunicationWithConfig = undefined;
    ceuList: CeuUnit[] = undefined;
    isOpenExpand: boolean[] = [];
    isOpenExtCommExpand: boolean = false;
    doTerminalText: string = '';
    isLoading: boolean = true;
    canEditSettings: boolean = false;
    isEditable: boolean = false;

    private destroySubscriptionSubject$ = new Subject<void>();

    constructor(
        private dialog: MatDialog,
        private fcuUnitService: FcuUnitServiece,
        private vesselService: VesselService,
        private router: Router,
        private route: ActivatedRoute,
        private externalCommunicationService: ExternalCommunicationService,
        private ceuUnitService: CeuUnitService,
        private fcuUnitSocketService: FcuUnitSocketService,
        private messageDialogService: MessageDialogService
    ) {}

    async ngOnInit() {
        this.fcuUnitSocketService
            .fcuUnitUpdated$()
            .pipe(takeUntil(this.destroySubscriptionSubject$))
            .subscribe(updatedFcuUnitFull => {
                if (updatedFcuUnitFull.fcuUnit.id !== this.selectedFcuUnit.id) return;
                this.initFcuUnitData(updatedFcuUnitFull);
                this.showMessageDialog('system-settings.fcu.message-dialog.update.message', {
                    index: this.selectedFcuUnit.index,
                });
            });

        this.vesselService.accessUpdated$.pipe(takeUntil(this.destroySubscriptionSubject$)).subscribe(canEdit => {
            this.canEditSettings = canEdit;
        });

        const vesselId = this.route.snapshot.params['vesselId'];
        const fcuId = this.route.snapshot.paramMap.get('fcuId');
        const urlState: any = history.state;
        this.isEditable = urlState?.isEditable ?? false;

        try {
            const currentVessel = await this.vesselService.getVessel(vesselId);
            if (!currentVessel) {
                this.router.navigateByUrl('home');
                return;
            }
            this.vesselService.setActiveVessel(currentVessel);
            this.vessel = currentVessel;

            this.fcuUnitList = await this.fcuUnitService.getList(this.vessel.id);

            if (fcuId) {
                const fcuUnitFull = await this.fcuUnitService.getFcuUnitFull(fcuId, [
                    FcuUnitAliases.fuUnits,
                    FcuUnitAliases.slots,
                    FcuUnitAliases.extComms,
                    FcuUnitAliases.ceuUnits,
                ]);
                this.initFcuUnitData(fcuUnitFull);
            } else {
                this.backFcuSetting();
            }

            if (urlState?.showMessage) {
                if (urlState.isNewFcu) {
                    this.showMessageDialog('system-settings.fcu.message-dialog.create.message', {
                        index: this.selectedFcuUnit?.index,
                    });
                } else {
                    this.showMessageDialog('system-settings.fcu.message-dialog.update.message', {
                        index: this.selectedFcuUnit?.index,
                    });
                }
                history.replaceState({}, '');
            }
        } catch (error) {
            console.log(error);
        } finally {
            this.isLoading = false;
        }
    }

    ngOnDestroy(): void {
        this.destroySubscriptionSubject$.next();
        this.destroySubscriptionSubject$.complete();
    }

    initFcuUnitData(fcuUnitFull: FcuUnitFull) {
        const { fcuUnit, slotList, fuUnitList, extComm, ceuUnitList } = fcuUnitFull;

        if (fcuUnit) {
            this.selectedFcuUnit = {
                id: fcuUnit.id,
                vesselId: fcuUnit.vesselId,
                location: fcuUnit.location,
                index: fcuUnit.index,
            };
            this.location = fcuUnit?.location;
            this.fcuALineAIp = fcuUnit?.network.A.LineAIp;
            this.fcuALineBIp = fcuUnit?.network.A.LineBIp;
            this.fcuAFieldNetLineAIp = fcuUnit?.network.A.fieldNetLineAIp;
            this.fcuAFieldNetLineBIp = fcuUnit?.network.A.fieldNetLineBIp;
            this.fcuACeuCommLineAIp = fcuUnit.network.A.ceuCommLineAIp;
            this.fcuACeuCommLineBIp = fcuUnit.network.A.ceuCommLineBIp;
            this.fcuBLineAIp = fcuUnit?.network.B.LineAIp;
            this.fcuBLineBIp = fcuUnit?.network.B.LineBIp;
            this.fcuBFieldNetLineAIp = fcuUnit?.network.B.fieldNetLineAIp;
            this.fcuBFieldNetLineBIp = fcuUnit?.network.B.fieldNetLineBIp;
            this.fcuBCeuCommLineAIp = fcuUnit.network.B.ceuCommLineAIp;
            this.fcuBCeuCommLineBIp = fcuUnit.network.B.ceuCommLineBIp;
        }

        if (slotList) {
            const { fcuSlotList, fuGroupSlotList } = this.splitSlots(slotList);

            const notEmptySlotNum = fcuSlotList.filter((slot: any) => slot.enable).length;
            this.slotList = fcuSlotList.map((slot: any) => ({
                ...slot,
                ioType: slot.ioType === this.EMPTY_VALUE ? this.EMPTY_SLOT : slot.ioType,
            }));
            this.selectedSlotCounter = notEmptySlotNum === 0 ? undefined : notEmptySlotNum;
            this.fuList = fuUnitList
                .map(fuUnit => {
                    return {
                        ...fuUnit,
                        slotList: fuGroupSlotList[fuUnit.id].map((slot: any) => ({
                            ...slot,
                            ioType: slot.ioType === this.EMPTY_VALUE ? this.EMPTY_SLOT : slot.ioType,
                        })),
                    };
                })
                .sort((a, b) => a.index - b.index);
        }

        if (extComm) {
            this.externalCommunication = extComm[0];
        }
        if (ceuUnitList) {
            this.ceuList = ceuUnitList.sort((a, b) => a.index - b.index);
        }
        this.isOpenExpand = Array.from(this.ceuList, () => false);
    }

    splitSlots(selectedSlotList: Slot[]) {
        const fcuSlotList = selectedSlotList.filter(slot => slot.fuId == null).sort((a, b) => a.no - b.no);

        const fuGroupSlotList = selectedSlotList
            .filter(slot => slot.fuId != null)
            .sort((a, b) => a.no - b.no)
            .reduce(
                (prev, slot) => {
                    const key = String(slot.fuId);
                    (prev[key] ??= []).push(slot);
                    return prev;
                },
                {} as Record<string, Slot[]>
            );

        return { fcuSlotList, fuGroupSlotList };
    }

    async openExternalCommunicationDialog() {
        return;
        this.isOpenOption = false;
        const dialogRef = this.dialog.open(ExternalCommunicationDialogComponent, {
            data: {
                fcuIndex: this.selectedFcuUnit?.index ? this.selectedFcuUnit?.index : this.fcuUnitList.length + 1,
                externalCommunication: this.externalCommunication,
                ceuList: this.ceuList,
                save: true,
            },
            disableClose: true,
        });

        try {
            const dialogResult = await firstValueFrom(dialogRef.afterClosed());

            this.isLoading = true;

            // if (dialogResult) {
            //     if (this.externalCommunication?.id) {
            //         await this.externalCommunicationService.deleteExtComm(this.externalCommunication.id);
            //     }
            //     await this.ceuUnitService.deleteByFcuId(this.selectedFcuUnit.id, this.selectedFcuUnit.index);

            //     this.externalCommunication = dialogResult.externalCommunication;
            //     this.ceuList = dialogResult.ceuList;

            //     if (this.externalCommunication?.index) {
            //         await this.externalCommunicationService.create({
            //             Ports: this.externalCommunication.Ports ? this.externalCommunication.Ports : [],
            //             index: 1,
            //             fcuId: this.selectedFcuUnit.id,
            //             vesselId: this.vessel.id,
            //         });
            //     }

            //     if (this.ceuList && this.ceuList?.length !== 0) {
            //         await this.ceuUnitService.create(
            //             this.ceuList.map(ceu => ({
            //                 ...ceu,
            //                 fcuId: this.selectedFcuUnit.id,
            //                 vesselId: this.vessel.id,
            //             })),
            //             this.selectedFcuUnit.index
            //         );
            //     }

            //     await this.fcuUnitService.getUpdatedFcuUnitFull(this.selectedFcuUnit.id, [
            //         FcuUnitAliases.ceuUnits,
            //         FcuUnitAliases.extComms,
            //     ]);
            // }
        } catch (error) {
            console.log(error);
        } finally {
            this.isLoading = false;
        }
    }

    backFcuSetting() {
        this.router.navigate([`home/${this.vessel.id}/settings`], {
            queryParams: { tab: SystemSettingTab.fcu },
        });
    }

    editFcuUnit() {
        if (!this.canEditSettings) return;
        this.router.navigateByUrl(`${this.vessel.id}/fcu/${this.selectedFcuUnit.id}`, {
            state: { origin: 'view' },
        });
    }

    openExpand(index: number, all: boolean) {
        if (all) {
            const openingExpand = this.isOpenExpand.filter(isOpenExpand => isOpenExpand);

            if (openingExpand?.length !== this.isOpenExpand.length) {
                this.isOpenExpand = this.isOpenExpand.map(() => true);
                this.isOpenExtCommExpand = true;
            } else {
                this.isOpenExpand = this.isOpenExpand.map(isOpenExpand => !isOpenExpand);
                this.isOpenExtCommExpand = !this.isOpenExtCommExpand;
            }
        } else {
            this.isOpenExpand[index] = !this.isOpenExpand[index];
        }
    }

    openExtCommExpand() {
        this.isOpenExtCommExpand = !this.isOpenExtCommExpand;
    }

    doTitle(slot: Slot): string {
        if (slot.ioType != 'DO') return '';
        if (!slot.terminal) return '';

        let title = slot.terminal.reduce((prev, terminal, index) => {
            if (index !== slot.terminal.length - 1) {
                return prev + String(terminal.num) + ', ';
            } else {
                return prev + String(terminal.num);
            }
        }, '(');

        title += ')';

        return title;
    }

    showMessageDialog(message: string, params?: Record<string, any>) {
        this.messageDialogService.openMessage(message, params);
    }

    get isAllExpanded() {
        return this.isOpenExpand.every(isOpenExpand => isOpenExpand) && this.isOpenExtCommExpand;
    }
}
